import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SERVICES = [
  {
    icon: 'target',
    title: 'Lead Generation',
    slug: 'lead-generation',
    description: "High-intent leads sourced from the platforms your customers actually use. We don't guess — we research, test, and scale what works.",
    features: [
      'Targeted audience research & ICP profiling',
      'Lead magnet creation & distribution',
      'Landing page design & optimization',
      'Multi-channel paid ad campaigns',
    ],
  },
  {
    icon: 'zap',
    title: 'Sales Funnels',
    slug: 'sales-funnels',
    description: "Static websites don't sell. We architect VSLs, booking flows, and email sequences engineered to convert cold traffic into clients.",
    features: [
      'Full funnel strategy & mapping',
      'VSL scripting & production direction',
      'Email automation sequences',
      'A/B testing & ongoing CRO',
    ],
  },
  {
    icon: 'filter',
    title: 'CRM & AI Automation',
    slug: 'crm-automation',
    description: "Never lose a lead to slow follow-up again. Our AI-powered automation keeps your pipeline moving around the clock — no manual effort needed.",
    features: [
      'CRM implementation & custom setup',
      'AI-powered chatbots & lead qualifiers',
      'Workflow automation & triggers',
      'Customer data analytics & reporting',
    ],
  },
  {
    icon: 'globe',
    title: 'Web Development',
    slug: 'web-development',
    description: "High-converting, performance-optimized websites and applications built for one goal: turning visitors into qualified leads and paying clients.",
    features: [
      'Responsive, conversion-first web design',
      'Landing pages & VSL pages',
      'Core Web Vitals & SEO optimization',
      'Third-party integrations & APIs',
    ],
  },
];

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const ICONS = { target: '🎯', zap: '⚡', filter: '🤖', globe: '💻' };

  return (
    <section id="services" className="relative py-24 px-6 bg-[#020617] overflow-hidden">
      <style>{`
        @keyframes svcFadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .svc-animate { opacity: 0; }
        .svc-animate.visible { animation: svcFadeUp 0.7s ease-out forwards; }
        .svc-card {
          background: #111620;
          border: 1px solid rgba(255,255,255,0.07);
          position: relative;
          overflow: hidden;
          transition: background 0.3s;
        }
        .svc-card:hover { background: #161d2b; }
        .svc-card:hover .svc-arrow { opacity: 1; transform: translate(0,0); }
        .svc-arrow {
          position: absolute; top: 2rem; right: 2rem;
          font-size: 1.2rem; color: #007bff;
          opacity: 0;
          transform: translate(-4px, 4px);
          transition: opacity 0.3s, transform 0.3s;
        }
        .svc-icon {
          width: 52px; height: 52px;
          background: rgba(0,123,255,0.1);
          border: 1px solid rgba(0,123,255,0.2);
          border-radius: 4px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.4rem;
          margin-bottom: 1.8rem;
        }
        .svc-feature::before {
          content: '→';
          color: #007bff;
          margin-right: 0.5rem;
          font-size: 0.75rem;
        }
      `}</style>

      <div className="max-w-7xl mx-auto relative z-10" ref={sectionRef}>
        {/* Section Header */}
        <div className="mb-12">
          <p className={`svc-animate ${isVisible ? 'visible' : ''} text-xs font-bold uppercase tracking-[0.18em] text-[#007bff] mb-3`}>
            Our Growth Systems
          </p>
          <h2
            className={`svc-animate ${isVisible ? 'visible' : ''} text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight`}
            style={isVisible ? { animationDelay: '0.1s' } : {}}
          >
            Comprehensive Solutions<br />Designed to Drive Revenue
          </h2>
          <p
            className={`svc-animate ${isVisible ? 'visible' : ''} text-gray-400 text-base max-w-xl`}
            style={isVisible ? { animationDelay: '0.2s' } : {}}
          >
            Every service is built as a system, not a one-off campaign — designed to compound results over time.
          </p>
        </div>

        {/* 2×2 Grid */}
        <div
          className={`svc-animate ${isVisible ? 'visible' : ''} grid md:grid-cols-2`}
          style={isVisible ? { animationDelay: '0.3s', gap: '1.5px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)' } : { gap: '1.5px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          {SERVICES.map((service, index) => (
            <article key={index} className="svc-card p-12">
              <span className="svc-arrow" aria-hidden="true">↗</span>
              <div className="svc-icon" aria-hidden="true">{ICONS[service.icon]}</div>
              <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">{service.description}</p>
              <ul className="space-y-2 mb-8">
                {service.features.map((f, i) => (
                  <li key={i} className="svc-feature text-xs text-gray-500">{f}</li>
                ))}
              </ul>
              <a
                href={`/services/${service.slug}`}
                onClick={e => { e.preventDefault(); navigate(`/services/${service.slug}`); }}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#007bff] hover:text-[#3399ff] transition-colors"
              >
                Learn More →
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
